// skills/pyre/pyreUI.js
// Pyre UI - Crafting-style card layout

import { pyreSkill } from './pyre.js';
import { LOGS, TOOLS, RITUALS, getLogsForRitual, getRitualAshesOutput } from './pyreData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { formatWithCommas } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getItemName, getItemIcon } from '../../data/items/index.js';
import { getSkillIcon } from '../../core/skillRegistry.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen, getActiveSkillId } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus, getTalentLootBonus } from '../../systems/talentTree/talentTree.js';
import { getAtlasPercentBg } from '../../data/atlasManifest.js';

// Track loot multiplier to detect corruption changes
let lastLootMultiplier = 1;

// Track current tab
let currentTab = 'logs';

// Fire icon sprite for card headers (lazy — atlas loads after module import)
let _fireSpriteCache = null;
function getFireIconSprite() {
    if (_fireSpriteCache) return _fireSpriteCache;
    const sheet = 'assets/icons/ui_icons/fire.png';
    const atlas = getAtlasPercentBg(sheet);
    _fireSpriteCache = atlas
        ? `<span class="sprite-icon sprite-icon-lg fire-sprite" style="background-image: url('${atlas.atlas}'); background-size: ${atlas.bgSize}; background-position: ${atlas.bgPosition}; width: 64px; height: 64px;"></span>`
        : `<span class="sprite-icon sprite-icon-lg fire-sprite" style="background-image: url('${sheet}'); background-position: 0px 0px; width: 64px; height: 64px; background-size: contain;"></span>`;
    return _fireSpriteCache;
}

/**
 * Render compact header with tool upgrades
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.PYRE);
    const xpPerHour = getXPPerHour(SKILLS.PYRE);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.PYRE);

    // Tool info
    const activeTool = pyreSkill.getActiveBuff();
    const tools = Object.values(TOOLS);
    const currentIndex = tools.findIndex(t => t.id === activeTool.id);
    const nextTool = tools[currentIndex + 1];
    const playerGold = gameState.getGold();

    let upgradeHtml = '';
    if (nextTool) {
        const canAfford = playerGold >= nextTool.price;
        if (skill.level < nextTool.level) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextTool.name} (${nextTool.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextTool.level}</span>
                </span>
            `;
        } else {
            const priceClass = canAfford ? 'affordable' : 'cant-afford';
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.pyreUI.upgradeTool('${nextTool.id}')">
                    → ${nextTool.name} (${nextTool.speedMultiplier}x)
                    <span class="upgrade-cost ${priceClass}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ Max</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.PYRE)}</span>
                    <span class="compact-name">Pyre</span>
                    ${renderSkillInfo(SKILLS.PYRE)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.PYRE)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Tool:</span>
                    <span class="tool-current">${activeTool.name} (${activeTool.speedMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render a single pyre log card
 */
function renderLogCard(logs, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= logs.level;
    const isActive = currentAction?.type === 'pyre' && currentAction?.log?.id === logs.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = pyreSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.PYRE);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.PYRE);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = logs.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with talent and corruption bonuses
    const talentXPBonus = getTalentXPBonus(SKILLS.PYRE);
    let effectiveXP = logs.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drop quantities (talent + corruption)
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.PYRE);
    const talentLootBonus = getTalentLootBonus(SKILLS.PYRE);

    // Check if player has required logs
    const playerLogCount = gameState.getItemCount(logs.inputItem);
    const hasLogs = playerLogCount >= logs.inputQuantity;

    // Input material display
    const inputIcon = getItemIcon(logs.inputItem);
    const inputName = getItemName(logs.inputItem);

    // Output display (ashes)
    const drop = logs.drops[0];
    const outputIcon = getItemIcon(drop.itemId);
    const outputName = getItemName(drop.itemId);
    const baseQty = drop.quantity;
    const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;
    const qtyDisplay = isBoosted
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    const cardClasses = [
        'pyre-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasLogs && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-log-id="${logs.id}"
             style="--log-color: ${logs.color}">

            <div class="pyre-card-header">
                <span class="pyre-card-level">${isUnlocked ? `Lvl ${logs.level}` : `Req: ${logs.level}`}</span>
                <span class="pyre-card-icon">${getFireIconSprite()}</span>
                <span class="pyre-card-name">${logs.name}</span>
            </div>

            <div class="pyre-card-info">
                <div class="pyre-stat">
                    <span class="stat-icon pyre-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="pyre-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="pyre-materials">
                <div class="materials-label">Input</div>
                <div class="material-row ${!hasLogs ? 'missing' : ''}">
                    <span class="material-icon">${inputIcon}</span>
                    <span class="material-name">${inputName}</span>
                    <span class="material-count">${playerLogCount}/${logs.inputQuantity}</span>
                </div>
            </div>

            <div class="pyre-output">
                <div class="output-label">Output</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="pyre-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.pyreUI.toggleBurning('${logs.id}')">
                        ${isActive ? 'Stop' : 'Burn'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="pyre-progress">
                <div class="pyre-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render a ritual card (log + bone combination)
 */
function renderRitualCard(ritual, logs, playerLevel, currentAction) {
    const isRitualUnlocked = playerLevel >= ritual.level;
    const isLogUnlocked = playerLevel >= logs.level;
    const isUnlocked = isRitualUnlocked && isLogUnlocked;

    const isActive = currentAction?.type === 'pyre_ritual' &&
                     currentAction?.log?.id === logs.id &&
                     currentAction?.ritual?.id === ritual.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = pyreSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.PYRE);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.PYRE);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = logs.baseTime / totalSpeedMultiplier;

    // Calculate ritual XP (base * ritual multiplier * corruption)
    const baseRitualXP = Math.floor(logs.xp * ritual.xpMultiplier);
    const effectiveXP = corruptionXPMult > 1 ? Math.floor(baseRitualXP * corruptionXPMult) : baseRitualXP;
    const bonusPercent = Math.round((ritual.xpMultiplier - 1) * 100);

    // Loot multiplier for drop quantities (talent + corruption)
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.PYRE);
    const talentLootBonus = getTalentLootBonus(SKILLS.PYRE);

    // Check materials
    const playerLogCount = gameState.getItemCount(logs.inputItem);
    const playerBoneCount = gameState.getItemCount(ritual.boneId);
    const hasLogs = playerLogCount >= logs.inputQuantity;
    const hasBones = playerBoneCount >= 1;
    const hasMaterials = hasLogs && hasBones;

    // Input icons
    const logIcon = getItemIcon(logs.inputItem);
    const logName = getItemName(logs.inputItem);
    const boneIcon = getItemIcon(ritual.boneId);

    // Output display (ashes - scaled by log tier for rituals)
    const outputIcon = getItemIcon('ashes');
    const outputName = getItemName('ashes');
    const baseQty = getRitualAshesOutput(logs.tier);
    const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;
    const qtyDisplay = isBoosted
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    const cardClasses = [
        'pyre-card',
        'ritual-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasMaterials && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    // Lock reason
    let lockReason = '';
    if (!isRitualUnlocked) {
        lockReason = `Req: Pyre ${ritual.level}`;
    } else if (!isLogUnlocked) {
        lockReason = `Req: Pyre ${logs.level}`;
    }

    return `
        <div class="${cardClasses}"
             data-log-id="${logs.id}"
             data-ritual-id="${ritual.id}"
             style="--log-color: ${ritual.color}">

            <div class="pyre-card-header">
                <span class="pyre-card-level">${isUnlocked ? `+${bonusPercent}% XP` : lockReason}</span>
                <span class="pyre-card-icon">${getFireIconSprite()}</span>
                <span class="pyre-card-name">${ritual.name}</span>
            </div>

            <div class="pyre-card-info">
                <div class="pyre-stat">
                    <span class="stat-icon pyre-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="pyre-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="pyre-materials">
                <div class="materials-label">Materials</div>
                <div class="material-row ${!hasLogs ? 'missing' : ''}">
                    <span class="material-icon">${logIcon}</span>
                    <span class="material-name">${logName}</span>
                    <span class="material-count">${playerLogCount}/${logs.inputQuantity}</span>
                </div>
                <div class="material-row ${!hasBones ? 'missing' : ''}">
                    <span class="material-icon">${boneIcon}</span>
                    <span class="material-name">${ritual.boneName}</span>
                    <span class="material-count bone-count">${playerBoneCount}/1</span>
                </div>
            </div>

            <div class="pyre-output">
                <div class="output-label">Output</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="pyre-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.pyreUI.toggleRitual('${logs.id}', '${ritual.id}')">
                        ${isActive ? 'Stop' : 'Perform'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="pyre-progress">
                <div class="pyre-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render tab navigation
 */
function renderTabs() {
    return `
        <div class="pyre-tabs">
            <button class="pyre-tab ${currentTab === 'logs' ? 'active' : ''}"
                    onclick="window.pyreUI.switchTab('logs')">
                🪵 Logs
            </button>
            <button class="pyre-tab ${currentTab === 'rituals' ? 'active' : ''}"
                    onclick="window.pyreUI.switchTab('rituals')">
                🦴 Rituals
            </button>
        </div>
    `;
}

/**
 * Render the pyre interface
 */
export function renderPyreUI() {
    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.PYRE);
    const currentAction = gameState.getCurrentAction();

    // Update loot multiplier tracker
    lastLootMultiplier = getCorruptionLootMultiplier(SKILLS.PYRE);

    let contentHtml = '';

    if (currentTab === 'logs') {
        // Standard log burning
        const logsHtml = Object.values(LOGS)
            .map(logs => renderLogCard(logs, skill.level, currentAction))
            .join('');
        contentHtml = `<div class="pyre-grid">${logsHtml}</div>`;
    } else if (currentTab === 'rituals') {
        // Flat grid of all ritual+log combinations (similar to Logs tab)
        const rituals = Object.values(RITUALS);
        const allRitualCards = [];

        // Collect all ritual+log cards
        for (const ritual of rituals) {
            const allowedLogs = getLogsForRitual(ritual);
            for (const log of allowedLogs) {
                allRitualCards.push(renderRitualCard(ritual, log, skill.level, currentAction));
            }
        }

        contentHtml = `<div class="pyre-grid">${allRitualCards.join('')}</div>`;
    }

    container.innerHTML = `
        <div class="pyre-interface">
            ${renderCompactHeader()}
            ${renderTabs()}
            ${contentHtml}
        </div>
    `;
}

/**
 * Switch between tabs
 */
export function switchTab(tabId) {
    currentTab = tabId;
    renderPyreUI();
}

/**
 * Toggle burning on/off for logs
 */
export function toggleBurning(logsId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.log?.id === logsId && currentAction.type === 'pyre') {
        pyreSkill.stopBurning();
    } else {
        const result = pyreSkill.canBurn(logsId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            pyreSkill.startBurning(logsId);
        }
    }

    renderPyreUI();
}

/**
 * Toggle ritual on/off
 */
export function toggleRitual(logId, ritualId) {
    const currentAction = gameState.getCurrentAction();

    // Check if this specific ritual is active
    if (currentAction &&
        currentAction.type === 'pyre_ritual' &&
        currentAction.log?.id === logId &&
        currentAction.ritual?.id === ritualId) {
        pyreSkill.stopBurning();
    } else {
        const result = pyreSkill.canPerformRitual(logId, ritualId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            pyreSkill.startRitual(logId, ritualId);
        }
    }

    renderPyreUI();
}

/**
 * Upgrade tool
 */
export function upgradeTool(toolId) {
    const result = pyreSkill.upgradeTool(toolId);

    if (result.success) {
        renderPyreUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Update pyre UI each tick
 */
export function updatePyreUI() {
    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.PYRE);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        if (!isTalentTreeOpen()) {
            renderPyreUI();
        }
        return;
    }

    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.PYRE);
    const xpPerHour = getXPPerHour(SKILLS.PYRE);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.PYRE);

    const xpTotalEl = document.querySelector('.pyre-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.pyre-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    const levelEl = document.querySelector('.pyre-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    updateCompactHeaderProgress('.pyre-interface', SKILLS.PYRE);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.pyre-interface');

    // Update material counts on all visible cards
    updateMaterialCounts();

    // Check for stale progress bars (action stopped)
    const action = gameState.getCurrentAction();
    const isPyreActive = action && (action.type === 'pyre' || action.type === 'pyre_ritual');

    const existingCards = document.querySelectorAll('.pyre-interface .pyre-card.active');
    if (existingCards.length > 0 && !isPyreActive) {
        renderPyreUI();
        return;
    }

    // Update action progress if pyre is active
    if (!isPyreActive) return;

    // For regular burning
    if (action.type === 'pyre') {
        const activeCard = document.querySelector(`.pyre-card[data-log-id="${action.log.id}"]:not(.ritual-card)`);
        if (activeCard) {
            const cardProgressFill = activeCard.querySelector('.pyre-progress-fill');
            if (cardProgressFill) {
                cardProgressFill.style.width = `${action.progress * 100}%`;
            }
        }
    }

    // For rituals
    if (action.type === 'pyre_ritual') {
        const activeCard = document.querySelector(`.pyre-card.ritual-card[data-log-id="${action.log.id}"][data-ritual-id="${action.ritual.id}"]`);
        if (activeCard) {
            const cardProgressFill = activeCard.querySelector('.pyre-progress-fill');
            if (cardProgressFill) {
                cardProgressFill.style.width = `${action.progress * 100}%`;
            }
        }
    }
}

/**
 * Update material counts on all visible cards
 */
function updateMaterialCounts() {
    const cards = document.querySelectorAll('.pyre-interface .pyre-card');

    cards.forEach(card => {
        const logId = card.dataset.logId;
        const ritualId = card.dataset.ritualId;
        if (!logId) return;

        const log = Object.values(LOGS).find(l => l.id === logId);
        if (!log) return;

        const playerLogCount = gameState.getItemCount(log.inputItem);
        const hasLogs = playerLogCount >= log.inputQuantity;

        // Update log count (first material-count element)
        const logCountEl = card.querySelector('.material-count:not(.bone-count)');
        if (logCountEl) logCountEl.textContent = `${playerLogCount}/${log.inputQuantity}`;

        // Update log material row
        const materialRows = card.querySelectorAll('.material-row');
        if (materialRows[0]) materialRows[0].classList.toggle('missing', !hasLogs);

        // Handle ritual cards with bone requirements
        if (ritualId) {
            const ritual = Object.values(RITUALS).find(r => r.id === ritualId);
            if (ritual) {
                const playerBoneCount = gameState.getItemCount(ritual.boneId);
                const hasBones = playerBoneCount >= 1;

                // Update bone count
                const boneCountEl = card.querySelector('.bone-count');
                if (boneCountEl) boneCountEl.textContent = `${playerBoneCount}/1`;

                // Update bone material row
                if (materialRows[1]) materialRows[1].classList.toggle('missing', !hasBones);

                // Update card no-materials state
                const hasMaterials = hasLogs && hasBones;
                card.classList.toggle('no-materials', !hasMaterials && !card.classList.contains('locked'));
            }
        } else {
            // Regular log card
            card.classList.toggle('no-materials', !hasLogs && !card.classList.contains('locked'));
        }
    });
}

/**
 * Setup pyre skill listeners
 */
export function initPyreUI() {
    pyreSkill.initializeStartingTool();

    // Callback for regular log burning
    pyreSkill.onBurningComplete = (logs, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Pyre level ${xpResult.newLevel}!`);
        }

        // Only re-render if pyre page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.PYRE && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderPyreUI();
            }
        }
    };

    // Callback for ritual completion
    pyreSkill.onRitualComplete = (logs, ritual, ritualXp, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Pyre level ${xpResult.newLevel}!`);
        }

        // Only re-render if pyre page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.PYRE && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderPyreUI();
            }
        }
    };

    // Listen for talent tree close to re-render pyre UI
    window.addEventListener('talentTreeClosed', (e) => {
        if (e.detail?.skillId === SKILLS.PYRE && getCurrentSkillId() === SKILLS.PYRE) {
            renderPyreUI();
        }
    });
}

if (typeof window !== 'undefined') {
    window.pyreUI = {
        toggleBurning,
        toggleRitual,
        upgradeTool,
        switchTab
    };
}

export default {
    renderPyreUI,
    toggleBurning,
    toggleRitual,
    upgradeTool,
    switchTab,
    updatePyreUI,
    initPyreUI
};
